import axios from 'axios';

/**
 * A failed form submission, split into the three places a form can show it:
 * a general banner, a message under a flat field, and a message under one
 * row of a list field (e.g. `steps[2].description`).
 */
export interface FormErrors {
  general: string[];
  fields: Record<string, string[]>;
  rows: Record<string, Record<number, Record<string, string[]>>>;
}

const GENERAL_KEYS = new Set(['error', 'detail', 'non_field_errors', 'message']);
const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

export function emptyFormErrors(): FormErrors {
  return { general: [], fields: {}, rows: {} };
}

export function hasFormErrors(errors: FormErrors | null | undefined): boolean {
  if (!errors) return false;
  return (
    errors.general.length > 0 ||
    Object.keys(errors.fields).length > 0 ||
    Object.keys(errors.rows).length > 0
  );
}

function messagesOf(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'string') {
    const text = value.trim();
    return text ? [text] : [];
  }
  if (Array.isArray(value)) return value.flatMap(messagesOf);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(messagesOf);
  }
  return [String(value)];
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Turn any request failure into `FormErrors`. Understands the shapes the
 * backend uses: `{error}` / `{detail}` / `{non_field_errors}`, DRF field
 * errors (`{title: ["..."]}`), and nested list errors
 * (`{steps: [{}, {description: ["..."]}]}`).
 */
export function parseFormErrors(error: unknown, fallback = DEFAULT_FALLBACK): FormErrors {
  const result = emptyFormErrors();

  if (axios.isAxiosError(error)) {
    if (!error.response) {
      result.general.push('Could not reach the server. Check your connection and try again.');
      return result;
    }
    const data: unknown = error.response.data;

    if (typeof data === 'string') {
      const text = data.trim();
      result.general.push(text && !text.startsWith('<') ? text : fallback);
      return result;
    }

    if (Array.isArray(data)) {
      result.general.push(...messagesOf(data));
    } else if (isPlainObject(data)) {
      for (const [key, value] of Object.entries(data)) {
        if (key === 'code') continue;
        if (GENERAL_KEYS.has(key)) {
          result.general.push(...messagesOf(value));
          continue;
        }
        if (Array.isArray(value) && value.some(isPlainObject)) {
          const rowErrors: Record<number, Record<string, string[]>> = {};
          const flat: string[] = [];
          value.forEach((item, index) => {
            if (isPlainObject(item)) {
              const row: Record<string, string[]> = {};
              for (const [rowKey, rowValue] of Object.entries(item)) {
                const messages = messagesOf(rowValue);
                if (messages.length) row[rowKey] = messages;
              }
              if (Object.keys(row).length) rowErrors[index] = row;
            } else {
              flat.push(...messagesOf(item));
            }
          });
          if (Object.keys(rowErrors).length) result.rows[key] = rowErrors;
          if (flat.length) result.fields[key] = flat;
          continue;
        }
        const messages = messagesOf(value);
        if (messages.length) result.fields[key] = messages;
      }
    }

    if (!hasFormErrors(result)) {
      result.general.push(
        error.response.status >= 500 ? 'The server could not complete the request. Please try again.' : fallback,
      );
    }
    return result;
  }

  if (error instanceof Error && error.message) {
    result.general.push(error.message);
    return result;
  }
  result.general.push(fallback);
  return result;
}

export function fieldErrorMessage(
  errors: FormErrors | null | undefined,
  field: string,
): string | undefined {
  const messages = errors?.fields[field];
  return messages?.length ? messages.join(' ') : undefined;
}

export function rowErrorMessage(
  errors: FormErrors | null | undefined,
  listField: string,
  index: number,
  field?: string,
): string | undefined {
  const row = errors?.rows[listField]?.[index];
  if (!row) return undefined;
  const messages = field ? row[field] ?? [] : Object.values(row).flat();
  return messages.length ? messages.join(' ') : undefined;
}

function labelFor(key: string, labels: Record<string, string>): string {
  if (labels[key]) return labels[key];
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Every message with a human label, for a summary banner near the submit button.
 * `rowLabel` names a list row when its position in the request is not the row
 * number the user sees (e.g. blank rows were left out of the payload).
 */
export function summarizeFormErrors(
  errors: FormErrors | null | undefined,
  labels: Record<string, string> = {},
  rowLabel?: (listField: string, index: number) => string | undefined,
): string[] {
  if (!errors) return [];
  const lines = [...errors.general];
  for (const [field, messages] of Object.entries(errors.fields)) {
    for (const message of messages) lines.push(`${labelFor(field, labels)}: ${message}`);
  }
  for (const [listField, rows] of Object.entries(errors.rows)) {
    for (const [index, row] of Object.entries(rows)) {
      const rowName =
        rowLabel?.(listField, Number(index)) ?? `${labelFor(listField, labels)} #${Number(index) + 1}`;
      for (const [field, messages] of Object.entries(row)) {
        for (const message of messages) {
          lines.push(`${rowName} ${labelFor(field, labels).toLowerCase()}: ${message}`);
        }
      }
    }
  }
  return lines;
}

/** Every message from a failed request as one line of text, for a banner or toast. */
export function formErrorText(
  error: unknown,
  fallback?: string,
  labels: Record<string, string> = {},
  rowLabel?: (listField: string, index: number) => string | undefined,
): string {
  return summarizeFormErrors(parseFormErrors(error, fallback), labels, rowLabel).join(' ');
}
